import React, { useState } from "react";
import { Leaf, ShieldCheck, CheckCircle2, ArrowRight, Truck, FileText, Lock, Building2, Phone, MessageSquare, ChevronDown } from "lucide-react";
import { Helmet } from "react-helmet";
import app from "./firebase";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";

const db = getFirestore(app);

export default function BuyerLandingPage() {
  const [formData, setFormData] = useState({
    commodity: "Cocoa Beans",
    volume: "",
    location: "",
    timeline: "Immediate (1-7 days)",
    purpose: "Export",
    companyName: "",
    contactName: "",
    phone: "",
    email: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, "buyer_leads"), {
        ...formData,
        createdAt: serverTimestamp(),
        source: "buyer_landing"
      });
    } catch (err) {
      console.warn("Firestore save fallback:", err);
    }
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div style={{ fontFamily: "var(--font-sans, system-ui, sans-serif)", color: "#1e293b", background: "#f8fafc", minHeight: "100vh" }}>
      <Helmet>
        <title>Request Verified Bulk Crop Suppliers — Agrivestia B2B Commodity Sourcing</title>
        <meta name="description" content="Source export-grade cocoa, raw cashew nuts, sesame seeds, and grains directly from verified African suppliers with escrow payment protection." />
        <meta name="keywords" content="buy bulk cocoa Nigeria, cashew suppliers Africa, sesame seeds export, wholesale agricultural commodities, verified farm suppliers" />
      </Helmet>

      {/* Header */}
      <header style={{ background: "#ffffff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={20} color="white" />
            </div>
            <span style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>AGRIVESTIA <span style={{ color: "#16a34a", fontSize: "14px", fontWeight: 700 }}>BUYERS</span></span>
          </a>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <a href="/contact" style={{ color: "#475569", textDecoration: "none", fontSize: "14px", fontWeight: 600 }}>Talk to Sourcing Team</a>
            <a href="#buyer-form" style={{ background: "#16a34a", color: "#ffffff", padding: "10px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px" }}>Request Verified Suppliers</a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{ background: "linear-gradient(180deg, #052e16 0%, #0f172a 100%)", color: "#ffffff", padding: "60px 24px 80px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "48px", alignItems: "center" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 700, color: "#4ade80", marginBottom: "20px" }}>
              <ShieldCheck size={16} /> B2B Commodity Sourcing & Escrow Trade
            </div>
            <h1 style={{ fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 900, lineHeight: 1.15, marginBottom: "20px", letterSpacing: "-0.02em" }}>
              Source Verified Bulk Crops from Trusted African Suppliers
            </h1>
            <p style={{ fontSize: "18px", color: "#94a3b8", lineHeight: 1.6, marginBottom: "32px" }}>
              Tell us your commodity, volume, and delivery destination. Agrivestia matches you with vetted local origin suppliers and protects your funds with bank-grade escrow until delivery inspection.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", fontSize: "14px", color: "#cbd5e1" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Pre-inspected Quality & Moisture</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Escrow Protected Funds</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Fast 1-Day Supplier Match</div>
            </div>
          </div>

          {/* Lead Form Card */}
          <div id="buyer-form" style={{ background: "#ffffff", borderRadius: "16px", padding: "32px", color: "#0f172a", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3)" }}>
            {submitted ? (
              <div style={{ textAlignment: "center", padding: "32px 16px" }}>
                <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "#dcfce7", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <CheckCircle2 size={36} />
                </div>
                <h3 style={{ fontSize: "24px", fontWeight: 800, marginBottom: "12px" }}>Sourcing Request Received!</h3>
                <p style={{ color: "#475569", fontSize: "15px", lineHeight: 1.6, marginBottom: "24px" }}>
                  Thank you, <strong>{formData.contactName || "Buyer"}</strong>. Our commodity desk has logged your request for <strong>{formData.volume} {formData.commodity}</strong>. A dedicated procurement specialist will contact you on WhatsApp/phone within 4 hours.
                </p>
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "8px", marginBottom: "20px", fontSize: "14px", color: "#334155" }}>
                  Want to browse live supplier tenders and active marketplace lots immediately?
                </div>
                <a href="/central_market" style={{ background: "#16a34a", color: "#ffffff", padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, display: "inline-block" }}>
                  View Live Central Market Tenders &rarr;
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "4px" }}>Request Verified Suppliers</h2>
                <p style={{ fontSize: "13px", color: "#64748b", margin: "-8px 0 8px" }}>Get matching suppliers, quality specs, and pricing within 24 hours.</p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Commodity Needed *</label>
                    <select name="commodity" value={formData.commodity} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                      <option>Cocoa Beans</option>
                      <option>Raw Cashew Nuts</option>
                      <option>White Sesame Seeds</option>
                      <option>Split Ginger</option>
                      <option>Cassava Flakes/Chips</option>
                      <option>Paddy Rice</option>
                      <option>Yellow Maize</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Quantity (Metric Tons) *</label>
                    <input type="text" name="volume" required placeholder="e.g. 50 MT" value={formData.volume} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Delivery Destination *</label>
                    <input type="text" name="location" required placeholder="e.g. Lagos Port / Europe" value={formData.location} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Buying Timeline</label>
                    <select name="timeline" value={formData.timeline} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                      <option>Immediate (1-7 days)</option>
                      <option>14-30 Days</option>
                      <option>Monthly Recurring Supply</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Buying Purpose</label>
                    <select name="purpose" value={formData.purpose} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                      <option>Export Trade</option>
                      <option>Local Industrial Processing</option>
                      <option>Wholesale Resale</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Company Name</label>
                    <input type="text" name="companyName" placeholder="AgriCorp Ltd" value={formData.companyName} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Your Full Name *</label>
                  <input type="text" name="contactName" required placeholder="John Doe" value={formData.contactName} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>WhatsApp / Phone *</label>
                    <input type="tel" name="phone" required placeholder="+234 801 234 5678" value={formData.phone} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Business Email *</label>
                    <input type="email" name="email" required placeholder="procurement@company.com" value={formData.email} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <button type="submit" disabled={loading} style={{ background: "#16a34a", color: "#ffffff", border: "none", padding: "14px", borderRadius: "8px", fontWeight: 800, fontSize: "16px", cursor: "pointer", marginTop: "8px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  {loading ? "Submitting Request..." : "Request Verified Suppliers Now"} <ArrowRight size={18} />
                </button>
                <div style={{ fontSize: "12px", color: "#94a3b8", textAlignment: "center" }}>
                  🔒 No account creation required upfront. Zero obligation quote.
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Trust & Verification Workflow */}
      <section style={{ padding: "80px 24px", maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 900, color: "#0f172a", marginBottom: "12px" }}>How Agrivestia Safeguards Commodity Buyers</h2>
          <p style={{ color: "#64748b", fontSize: "16px", maxWidth: "640px", margin: "0 auto" }}>Eliminating trade risks, quality discrepancies, and supplier default in West African agricultural commodity sourcing.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "24px" }}>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "18px", marginBottom: "16px" }}>01</div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Physical Supplier Audit</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Every supplier on our platform undergoes physical warehouse inspection, CAC registration check, and historical delivery verification before bidding on your request.</p>
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "18px", marginBottom: "16px" }}>02</div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Escrow Protection</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Your funds are held in secure escrow. Suppliers receive payment ONLY after independent quality inspection and waybill confirmation at your destination port or warehouse.</p>
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "18px", marginBottom: "16px" }}>03</div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Lab Quality Specs</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Pre-shipment moisture content tests, KOR (Outturn Ratio) grading, and impurity analysis certificates (SGS/Bureau Veritas compatible) for export compliance.</p>
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "18px", marginBottom: "16px" }}>04</div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Real-Time Logistics Tracking</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Track truck dispatch, transit waybill updates, and port arrival status from farmgate warehouse directly to your delivery warehouse.</p>
          </div>
        </div>
      </section>

      {/* Verified Crops & Specifications Table */}
      <section style={{ background: "#ffffff", padding: "60px 24px", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 900, color: "#0f172a", marginBottom: "8px" }}>Active Verified Export Commodity Specs</h2>
          <p style={{ color: "#64748b", fontSize: "15px", marginBottom: "32px" }}>Sample specifications and minimum order volumes available for immediate sourcing.</p>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0", color: "#334155" }}>
                  <th style={{ padding: "14px" }}>Commodity</th>
                  <th style={{ padding: "14px" }}>Grade / Quality Spec</th>
                  <th style={{ padding: "14px" }}>Min. Order Volume</th>
                  <th style={{ padding: "14px" }}>Primary Origin Hubs</th>
                  <th style={{ padding: "14px" }}>Fulfillment Time</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "14px", fontWeight: 700 }}>Raw Cashew Nuts</td>
                  <td style={{ padding: "14px", color: "#475569" }}>KOR 48-52 lbs, Moisture &lt; 8%, Nut Count 180-200/kg</td>
                  <td style={{ padding: "14px" }}>25 Metric Tons</td>
                  <td style={{ padding: "14px" }}>Ogbomoso, Oyo / Kogi State</td>
                  <td style={{ padding: "14px", color: "#16a34a", fontWeight: 700 }}>7–14 Days</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "14px", fontWeight: 700 }}>Fermented Cocoa Beans</td>
                  <td style={{ padding: "14px", color: "#475569" }}>Main Crop, Moisture &lt; 7.5%, Slaty &lt; 3%, Mold &lt; 3%</td>
                  <td style={{ padding: "14px" }}>20 Metric Tons</td>
                  <td style={{ padding: "14px" }}>Ondo, Osun / Cross River</td>
                  <td style={{ padding: "14px", color: "#16a34a", fontWeight: 700 }}>5–10 Days</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "14px", fontWeight: 700 }}>White Sesame Seeds</td>
                  <td style={{ padding: "14px", color: "#475569" }}>Purity 99.5%, Machine Cleaned, Oil Content &gt; 51%</td>
                  <td style={{ padding: "14px" }}>50 Metric Tons</td>
                  <td style={{ padding: "14px" }}>Kano, Jigawa / Benue</td>
                  <td style={{ padding: "14px", color: "#16a34a", fontWeight: 700 }}>10–18 Days</td>
                </tr>
                <tr>
                  <td style={{ padding: "14px", fontWeight: 700 }}>Split Dried Ginger</td>
                  <td style={{ padding: "14px", color: "#475569" }}>Moisture &lt; 9%, Volatile Oil &gt; 2%, Cleaned</td>
                  <td style={{ padding: "14px" }}>15 Metric Tons</td>
                  <td style={{ padding: "14px" }}>Kaduna / Nasarawa</td>
                  <td style={{ padding: "14px", color: "#16a34a", fontWeight: 700 }}>7–12 Days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "#052e16", color: "#94a3b8", padding: "40px 24px", textAlign: "center", fontSize: "14px" }}>
        <p>&copy; 2026 Agrivestia B2B Sourcing Platform. All rights reserved. | <a href="/" style={{ color: "#4ade80", textDecoration: "none" }}>Homepage</a> | <a href="/central_market" style={{ color: "#4ade80", textDecoration: "none" }}>Central Market</a> | <a href="/contact" style={{ color: "#4ade80", textDecoration: "none" }}>Contact Desk</a></p>
      </footer>
    </div>
  );
}
